import type { ItWalletAuthorizationServerMetadata } from "@pagopa/io-wallet-oid-federation";

import {
  CallbackContext,
  HashAlgorithm,
  calculateJwkThumbprint,
} from "@openid4vc/oauth2";
import { IoWalletSdkConfig } from "@pagopa/io-wallet-utils";

import { Oauth2Error } from "../errors";
import { verifyClientAttestationPopJwt } from "./client-attestation-pop";
import {
  oauthClientAttestationHeader,
  oauthClientAttestationPopHeader,
} from "./types";
import { verifyWalletAttestationJwt } from "./wallet-attestation";

export interface ClientAttestationOptions {
  /**
   * The client attestation DPoP JWT provided in the request.
   */
  clientAttestationPopJwt: string;

  /**
   * Whether to ensure that the key used in client attestation confirmation
   * is the same key used for DPoP. This only has effect if both DPoP and client
   * attestations are present.
   *
   * @default false
   */
  ensureConfirmationKeyMatchesDpopKey?: boolean;

  /**
   * The wallet attestation JWT provided in the request.
   */
  walletAttestationJwt: string;
}

export interface VerifyClientAttestationOptions {
  /**
   * The authorization server metadata.
   */
  authorizationServerMetadata: ItWalletAuthorizationServerMetadata;

  /**
   * Callbacks for hashing and JWT verification.
   */
  callbacks: Pick<CallbackContext, "hash" | "verifyJwt">;

  /**
   * The client attestation options.
   */
  clientAttestation: ClientAttestationOptions;

  /**
   * The IT-Wallet SDK configuration, used to select the correct version-specific
   * validation schema for the wallet attestation JWT.
   */
  config: IoWalletSdkConfig;

  /**
   * The DPoP JWK thumbprint value, if DPoP is being used in the request.
   */
  dpopJwkThumbprint?: string;

  /**
   * The current time to use when verifying the JWTs.
   * If not provided current time will be used.
   *
   * @default new Date()
   */
  now?: Date;

  /**
   * The client id provided in the authorization request, if any.
   *
   * Pass this when the authorization request includes a `client_id` parameter.
   * The function will verify that it matches the `sub` claim in the client attestation JWT.
   * If not provided, no client_id validation is performed.
   */
  requestClientId?: string;
}

/**
 * Verifies wallet attestation and client attestation PoP headers as a pair.
 *
 * @param options - Client attestation verification options.
 * @param options.authorizationServerMetadata - Authorization Server metadata used for issuer/audience checks.
 * @param options.callbacks - Hashing and JWT verification callbacks.
 * @param options.clientAttestation - Wallet attestation and PoP JWT values from the request.
 * @param options.config - IT-Wallet specification version used to validate the wallet attestation.
 * @param options.dpopJwkThumbprint - Optional DPoP JWK thumbprint to compare with the attestation key.
 * @param options.now - Date used for temporal validation.
 * @param options.requestClientId - Optional client_id that must match the attestation subject.
 * @returns Verified wallet attestation and client attestation PoP JWT data.
 * @throws {Oauth2Error} If required headers are missing or any attestation validation fails.
 */
export async function verifyClientAttestation(
  options: VerifyClientAttestationOptions,
) {
  if (
    !options.clientAttestation.walletAttestationJwt ||
    !options.clientAttestation.clientAttestationPopJwt
  ) {
    throw new Oauth2Error(
      `Missing required client attestation parameters in the request. Make sure to provide the '${oauthClientAttestationHeader}' and '${oauthClientAttestationPopHeader}' header values.`,
    );
  }

  const clientAttestation = await verifyWalletAttestationJwt({
    callbacks: options.callbacks,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    config: options.config as any,
    now: options.now,
    walletAttestationJwt: options.clientAttestation.walletAttestationJwt,
  });

  const clientAttestationPop = await verifyClientAttestationPopJwt({
    authorizationServer: options.authorizationServerMetadata.issuer,
    callbacks: options.callbacks,
    clientAttestationPopJwt: options.clientAttestation.clientAttestationPopJwt,
    clientAttestationPublicJwk: clientAttestation.payload.cnf.jwk,
    now: options.now,
  });

  if (
    options.requestClientId &&
    options.requestClientId !== clientAttestation.payload.sub
  ) {
    // Ensure the client id matches with the client id provided in the authorization request
    throw new Oauth2Error(
      `The client_id '${options.requestClientId}' in the request does not match the client id '${clientAttestation.payload.sub}' in the client attestation`,
    );
  }

  if (
    options.clientAttestation.ensureConfirmationKeyMatchesDpopKey &&
    options.dpopJwkThumbprint
  ) {
    const clientAttestationJkt = await calculateJwkThumbprint({
      hashAlgorithm: HashAlgorithm.Sha256,
      hashCallback: options.callbacks.hash,
      jwk: clientAttestation.payload.cnf.jwk,
    });

    if (clientAttestationJkt !== options.dpopJwkThumbprint) {
      throw new Oauth2Error(
        "Expected the DPoP JWK thumbprint value to match the JWK thumbprint of the client attestation confirmation JWK. Ensure both DPoP and client attestation use the same key.",
      );
    }
  }

  return {
    clientAttestation,
    clientAttestationPop,
  };
}
